'use client';

import { useReducedMotion } from 'motion/react';
import type { FocusEvent, MouseEvent, ReactNode, SyntheticEvent } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { cn } from '@/lib/ui/utils';

interface QuickAmountOption {
  label: string;
  value: number;
}

interface AmountInputProps {
  value: string;
  onChange: (value: string) => void;
  currencySymbol: string;
  /** Symbol rendered after the digits (e.g. '%'). */
  suffixSymbol?: string;
  /** Active token-percentage preset; omitted for one-shot currency shortcuts. */
  selectedQuickAmount?: number | 'max' | null | undefined;
  /** Called when the user presses Enter in the input. */
  onSubmit?: (() => void) | undefined;
  /**
   * Smaller hero — shorter box + smaller digits. For form contexts (e.g. the
   * gift composer) where the full-height trade hero reads as oversized.
   */
  compact?: boolean;
  disabled?: boolean;
  /** Accessible label for the hidden input. */
  inputLabel: string;
  /** The caller owns localized string validation and ungrouped presentation. */
  localizedInput?: boolean;
  inputMode?: 'decimal' | 'none';
  ariaInvalid?: boolean;
  onBlur?(): void;
  /** Disable the quick-amount and Max controls without disabling amount entry. */
  quickAmountsDisabled?: boolean;
  /** Optional content rendered between the input and the quick-amount buttons. */
  belowInput?: ReactNode;
}

type AnimatedAmountInputProps = AmountInputProps &
  (
    | {
        showQuickAmounts: false;
        quickAmounts?: readonly QuickAmountOption[];
        onQuickAmount?: (amount: number) => void;
        onMaxClick?: () => void;
        maxLabel?: string;
      }
    | {
        showQuickAmounts?: true;
        quickAmounts: readonly QuickAmountOption[];
        onQuickAmount(amount: number): void;
        onMaxClick(): void;
        maxLabel: string;
      }
  );

/** A character slot with a stable ID so it stays in place during exit animations. */
interface CharSlot {
  char: string;
  id: number;
  state: 'stable' | 'entering' | 'exiting';
  delayMs?: number;
}

const QUICK_AMOUNT_BUTTON_CLASSNAME =
  'shrink-0 rounded-full border-border-primary/60 px-3.5 font-semibold transition-all duration-fast hover:bg-background-tertiary/70' as const;

let slotIdCounter = 0;

function nextSlotId(): number {
  slotIdCounter += 1;
  return slotIdCounter;
}

function selectFullAmount(e: MouseEvent<HTMLInputElement>): void {
  e.currentTarget.select();
}

function stableSlots(value: string): CharSlot[] {
  return value.split('').map((char) => ({ char, id: nextSlotId(), state: 'stable' }));
}

function diffSlots(prev: readonly CharSlot[], next: string, manualEdit: boolean): CharSlot[] {
  const live = prev.filter((slot) => slot.state !== 'exiting');
  const chars = next.split('');
  let prefixLength = 0;
  while (prefixLength < chars.length && live[prefixLength]?.char === chars[prefixLength]) {
    prefixLength += 1;
  }
  const entering = chars.slice(prefixLength).map(
    (char, index): CharSlot => ({
      char,
      id: nextSlotId(),
      state: 'entering',
      delayMs: manualEdit ? 0 : index * 15,
    }),
  );
  const replacement =
    chars.length === live.length && chars.every((char, index) => char !== live[index]?.char);
  const exiting =
    manualEdit || replacement
      ? []
      : [
          ...live.slice(prefixLength).map((slot): CharSlot => ({ ...slot, state: 'exiting' })),
          ...prev.filter((slot) => slot.state === 'exiting'),
        ];
  // Retain each entering digit's identity and delay until its own animation ends.
  return [...live.slice(0, prefixLength), ...exiting, ...entering];
}

interface AnimatedDigitSlotsProps {
  slots: readonly CharSlot[];
  caretPosition: number;
  showCaret: boolean;
  compact: boolean;
  grouped: boolean;
  onAnimationComplete: (id: number) => void;
}

function AnimatedDigitSlots({
  slots,
  caretPosition,
  showCaret,
  compact,
  grouped,
  onAnimationComplete,
}: AnimatedDigitSlotsProps): React.ReactNode {
  let integerLength = 0;
  for (const slot of slots) {
    if (slot.state === 'exiting') continue;
    if (slot.char === '.') break;
    integerLength += 1;
  }
  let visibleSlotIndex = 0;

  function renderCaret(): ReactNode {
    return (
      <span
        key="caret"
        className={cn(
          'animate-caret-blink ml-px inline-block w-0.5 self-center rounded-full bg-foreground-primary',
          compact ? 'h-6' : 'h-7.5',
        )}
      />
    );
  }

  const amountDigits = slots.flatMap((slot): ReactNode[] => {
    const isVisible = slot.state !== 'exiting';
    const shouldRenderCaret = showCaret && isVisible && visibleSlotIndex === caretPosition;
    const hasSeparator =
      grouped &&
      isVisible &&
      visibleSlotIndex > 0 &&
      visibleSlotIndex < integerLength &&
      (integerLength - visibleSlotIndex) % 3 === 0;
    if (isVisible) {
      visibleSlotIndex += 1;
    }

    let renderedSlot: ReactNode;
    if (slot.state === 'entering') {
      const delayMs = slot.delayMs ?? 0;
      renderedSlot = (
        <span
          key={slot.id}
          className="animate-digit-enter inline-block"
          onAnimationEnd={(): void => onAnimationComplete(slot.id)}
          ref={
            delayMs > 0
              ? (el: HTMLSpanElement | null): void => {
                  el?.style.setProperty('--enter-delay', `${String(delayMs)}ms`);
                }
              : undefined
          }
        >
          {slot.char}
        </span>
      );
    } else if (slot.state === 'exiting') {
      renderedSlot = (
        <span
          key={slot.id}
          className="animate-digit-exit pointer-events-none inline-block overflow-hidden"
          onAnimationEnd={(): void => onAnimationComplete(slot.id)}
        >
          {slot.char}
        </span>
      );
    } else {
      renderedSlot = (
        <span key={slot.id} className="inline-block">
          {slot.char}
        </span>
      );
    }

    // Group raw digit slots without changing their identity or the native cursor offsets.
    const separator = hasSeparator ? [<span key={`group-${slot.id}`}>,</span>] : [];
    return [...separator, ...(shouldRenderCaret ? [renderCaret()] : []), renderedSlot];
  });

  return showCaret && visibleSlotIndex === caretPosition
    ? [...amountDigits, renderCaret()]
    : amountDigits;
}

export function AnimatedAmountInput({
  value,
  onChange,
  currencySymbol,
  suffixSymbol,
  quickAmounts,
  onQuickAmount,
  onMaxClick,
  selectedQuickAmount,
  onSubmit,
  compact = false,
  disabled = false,
  inputLabel,
  maxLabel,
  showQuickAmounts,
  quickAmountsDisabled = false,
  belowInput,
  localizedInput = false,
  inputMode = 'decimal',
  ariaInvalid,
  onBlur,
}: AnimatedAmountInputProps): React.ReactNode {
  const amountTextClass = compact ? 'text-display' : 'text-display-lg';
  const containerRef = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);
  const [caretPosition, setCaretPosition] = useState(value.length);
  const digitsRef = useRef<HTMLDivElement>(null);

  const displayValue = value || '0';
  const isEmpty = value === '';

  const [slots, setSlots] = useState<CharSlot[]>(() => stableSlots(displayValue));
  const prevValueRef = useRef(value);
  const manualValueRef = useRef<string | null>(null);
  const [animateScale, setAnimateScale] = useState(false);
  const reduceMotion = useReducedMotion();

  // Reconcile before paint so the visible amount never trails the native input.
  useLayoutEffect(() => {
    const prev = prevValueRef.current;
    prevValueRef.current = value;
    if (prev === value && !reduceMotion) return;
    const manualEdit = manualValueRef.current === value;
    manualValueRef.current = null;
    setAnimateScale(!manualEdit && !reduceMotion);
    setSlots((current) =>
      reduceMotion || (manualEdit && value.length < prev.length)
        ? stableSlots(displayValue)
        : diffSlots(current, displayValue, manualEdit),
    );
  }, [displayValue, value, reduceMotion]);

  function completeDigitAnimation(id: number): void {
    setSlots((current) =>
      current.flatMap((slot) => {
        if (slot.id !== id) return [slot];
        return slot.state === 'exiting' ? [] : [{ ...slot, state: 'stable' }];
      }),
    );
  }

  // Measure and scale down only if the text overflows the container.
  // Sets transform directly on the DOM element to avoid inline style props.
  useLayoutEffect(() => {
    const container = containerRef.current;
    const digits = digitsRef.current;
    if (!container || !digits) return;
    const availableWidth = container.clientWidth - 32;
    const naturalWidth = digits.scrollWidth;
    if (naturalWidth > availableWidth && naturalWidth > 0) {
      const s = Math.max(0.5, availableWidth / naturalWidth);
      digits.style.transform = `scale(${String(s)})`;
    } else {
      digits.style.transform = '';
    }
  });

  function updateCaretPosition(input: HTMLInputElement): void {
    const selectionStart = input.selectionStart;
    if (selectionStart !== null) {
      setCaretPosition(selectionStart);
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const raw = e.target.value;
    if (localizedInput || (/^\d*\.?\d{0,2}$/.test(raw) && raw.replace('.', '').length <= 12)) {
      manualValueRef.current = raw;
      updateCaretPosition(e.currentTarget);
      onChange(raw);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter' && onSubmit !== undefined) {
      e.preventDefault();
      onSubmit();
    }
  }

  function handleSelect(e: SyntheticEvent<HTMLInputElement>): void {
    updateCaretPosition(e.currentTarget);
  }

  function handleFocus(e: FocusEvent<HTMLInputElement>): void {
    setFocused(true);
    updateCaretPosition(e.currentTarget);
  }

  const showCaret = focused && !disabled;
  const displayCaretPosition = isEmpty
    ? displayValue.length
    : Math.min(caretPosition, displayValue.length);

  return (
    <div className="flex flex-col gap-3">
      <div
        ref={containerRef}
        className={cn(
          'relative isolate flex items-center justify-center overflow-hidden rounded-overlay border px-4',
          compact ? 'h-16' : 'h-24',
          'transition-all duration-250 ease-default',
          disabled ? 'cursor-not-allowed' : 'cursor-text',
          focused
            ? 'border-border-primary bg-background-tertiary/20 ring-2 ring-border-primary/10'
            : 'border-transparent hover:border-border-primary/40',
        )}
      >
        <input
          type="text"
          inputMode={inputMode}
          pattern={localizedInput ? undefined : '[0-9]*\\.?[0-9]*'}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onSelect={handleSelect}
          onDoubleClick={selectFullAmount}
          onFocus={handleFocus}
          onBlur={(): void => {
            setFocused(false);
            onBlur?.();
          }}
          disabled={disabled}
          aria-label={inputLabel}
          aria-invalid={ariaInvalid}
          className={cn(
            'absolute inset-0 z-overlay size-full opacity-0',
            disabled ? 'cursor-not-allowed' : 'cursor-text',
          )}
          autoComplete="off"
        />
        <div
          ref={digitsRef}
          className={cn(
            'pointer-events-none flex origin-center items-baseline',
            animateScale && 'transition-transform duration-normal ease-default',
          )}
        >
          <span
            className={cn(
              'select-none font-semibold transition-colors duration-fast',
              amountTextClass,
              isEmpty ? 'text-foreground-secondary/30' : 'text-foreground-secondary/50',
            )}
            aria-hidden="true"
          >
            {currencySymbol}
          </span>

          <div className="relative">
            <span
              className={cn(
                'flex items-baseline font-semibold tabular-nums',
                amountTextClass,
                isEmpty ? 'text-foreground-secondary/30' : 'text-foreground-primary',
                disabled && 'cursor-not-allowed opacity-50',
              )}
              aria-hidden="true"
            >
              <AnimatedDigitSlots
                slots={slots}
                caretPosition={displayCaretPosition}
                showCaret={showCaret}
                compact={compact}
                grouped={!localizedInput}
                onAnimationComplete={completeDigitAnimation}
              />

              {suffixSymbol !== undefined && suffixSymbol.length > 0 && (
                <span
                  className={cn(
                    'select-none font-semibold transition-colors duration-fast',
                    isEmpty ? 'text-foreground-secondary/30' : 'text-foreground-secondary/50',
                  )}
                >
                  {suffixSymbol}
                </span>
              )}
            </span>
          </div>
        </div>
      </div>

      {belowInput}

      {/* `flex-wrap`: long currency symbols (e.g. "CN¥500") widen each chip past
          the container on one line — wrap to a second row instead of clipping. */}
      {showQuickAmounts !== false ? (
        <div
          data-testid="quick-amount-row"
          className="flex w-full min-w-0 flex-wrap items-center justify-center gap-1.5"
        >
          {quickAmounts.map((opt) => (
            <AnimatedButton
              key={opt.value}
              variant="secondary"
              size="sm"
              className={cn(
                QUICK_AMOUNT_BUTTON_CLASSNAME,
                'active:scale-95',
                selectedQuickAmount === opt.value &&
                  'bg-background-tertiary ring-1 ring-border-primary',
              )}
              aria-pressed={
                selectedQuickAmount === undefined ? undefined : selectedQuickAmount === opt.value
              }
              onClick={(): void => {
                onQuickAmount(opt.value);
              }}
              disabled={disabled || quickAmountsDisabled}
            >
              {opt.label}
            </AnimatedButton>
          ))}
          <AnimatedButton
            variant="secondary"
            size="sm"
            className={cn(
              QUICK_AMOUNT_BUTTON_CLASSNAME,
              'active:scale-95',
              selectedQuickAmount === 'max' && 'bg-background-tertiary ring-1 ring-border-primary',
            )}
            aria-pressed={
              selectedQuickAmount === undefined ? undefined : selectedQuickAmount === 'max'
            }
            onClick={(): void => {
              onMaxClick();
            }}
            disabled={disabled || quickAmountsDisabled}
          >
            {maxLabel}
          </AnimatedButton>
        </div>
      ) : null}
    </div>
  );
}
